import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';

/** Individuelle Vertraege (al-user-management Migration 081,
 *  PRD features/prd/custom-contracts.md). Der Vertrag haelt die Konditionen,
 *  ein privater Plan die Kontingente. */

/** Reihenfolge = Anzeige-Reihenfolge (contract_ops.LIMIT_SPECS). */
export const CONTRACT_LIMIT_KEYS = [
  'files',
  'storage_bytes',
  'urls',
  'moodle_courses',
  'bots',
  'credits_monthly',
  'tokens_monthly',
  'requests_monthly',
  'searches_monthly',
] as const;

export type ContractLimitKey = (typeof CONTRACT_LIMIT_KEYS)[number];

/** -1 = unbegrenzt, null = im Plan nicht vorhanden. */
export type ContractLimits = Partial<Record<ContractLimitKey, number | null>>;

/** Diese Grenzen kennen kein „unbegrenzt“ (al-tutor prueft bot_count >= limit). */
export const CONTRACT_LIMITS_WITHOUT_UNLIMITED: ContractLimitKey[] = ['bots'];

export type ContractBillingCycle = 'month' | 'quarter' | 'year';
export type ContractRenewal = 'auto' | 'none';
export type ContractInvoiceMode = 'approve' | 'auto_send';

export interface ContractUsageTerm {
  metric: 'credits';
  included_per_period: number;
  unit_size: number;
  unit_price_net_cents: number;
}

export interface ContractPlan {
  id: string;
  name: string;
  display_name?: string | null;
  is_contract: boolean;
  base_plan_id?: string | null;
  monthly_cost_net?: number | null;
  monthly_cost_gross?: number | null;
  /** Funktionen des Plans (clients.features-Werte). */
  features?: string[];
}

export interface ContractTemplatePlan extends ContractPlan {
  limits: ContractLimits;
}

/** Konditionen einer Vertragsvorlage — ohne Kundendaten. */
export interface ContractTemplateTerms {
  renewal?: ContractRenewal;
  notice_days?: number | null;
  billing_cycle?: ContractBillingCycle;
  base_fee_net_cents?: number;
  payment_terms_days?: number;
  invoice_mode?: ContractInvoiceMode;
  usage_terms?: ContractUsageTerm[];
  /** Laufzeit; setzt den letzten Vertragstag vor. */
  term_months?: number | null;
}

/** Gespeicherte Vertragsvorlage (Migration 083) — nur ein Startwert. */
export interface ContractTemplate {
  id: string;
  name: string;
  description?: string | null;
  limits: ContractLimits;
  features: string[];
  terms: ContractTemplateTerms;
  source_plan_id?: string | null;
  created_by_email?: string | null;
  updated_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ContractTemplateBody {
  name: string;
  description?: string | null;
  limits: ContractLimits;
  features: string[];
  terms: ContractTemplateTerms;
  source_plan_id?: string | null;
}

export interface ClientContract {
  id: string;
  client_id?: string;
  status: 'active' | 'ended';
  plan_id?: string | null;
  previous_plan_id?: string | null;
  display_name?: string | null;
  base_plan_id?: string | null;
  starts_on: string;
  ends_on?: string | null;
  renewal: ContractRenewal;
  notice_days?: number | null;
  billing_cycle: ContractBillingCycle;
  base_fee_net_cents: number;
  currency: string;
  payment_terms_days: number;
  invoice_email?: string | null;
  buyer_reference?: string | null;
  po_number?: string | null;
  usage_terms: ContractUsageTerm[];
  invoice_mode?: ContractInvoiceMode;
  /** Ab hier rechnet alphalearn selbst ab (Anker der Perioden). null = keine
   *  automatischen Rechnungen, z. B. solange Stripe noch abrechnet. */
  billing_starts_on?: string | null;
  note?: string | null;
  created_by_email?: string | null;
  ended_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  /** Nur in der Kundenansicht (`GET /clients/contract`). */
  limits?: ContractLimits;
}

export interface ContractAuditEntry {
  id: string;
  contract_id?: string | null;
  action: 'activated' | 'updated' | 'ended' | string;
  /** {"feld": [alt, neu]} */
  changes: Record<string, [any, any]>;
  note?: string | null;
  changed_by_email?: string | null;
  changed_by_name?: string | null;
  created_at?: string | null;
}

/** Status einer Vertragsrechnung (al-user-management client_invoices). */
export type ContractInvoiceStatus =
  | 'draft'
  | 'approved'
  | 'submitted'
  | 'issued'
  | 'sent'
  | 'paid'
  | 'overdue'
  | 'dunning'
  | 'cancelled'
  | 'failed'
  | 'discarded';

export interface ContractInvoiceLine {
  kind: 'base_fee' | 'usage' | 'manual' | string;
  description: string;
  quantity: number;
  unit?: string | null;
  unit_price_net_cents: number;
  amount_net_cents: number;
}

export interface ContractInvoice {
  id: string;
  kind: 'period' | 'manual';
  status: ContractInvoiceStatus;
  period_start?: string | null;
  service_start?: string | null;
  /** Halboffen [start, end) — der letzte Tag ist end - 1. */
  service_end?: string | null;
  usage_start?: string | null;
  usage_end?: string | null;
  lines: ContractInvoiceLine[];
  net_cents: number;
  total_gross_cents?: number | null;
  currency: string;
  /** Rechnungsnummer aus alphacount; null solange Entwurf. */
  number?: string | null;
  due_date?: string | null;
  sent_at?: string | null;
  paid_at?: string | null;
  dunning_level?: number | null;
  has_pdf: boolean;
  created_at?: string | null;
  // Nur in der Super-Admin-Ansicht:
  payment_terms_days?: number | null;
  note?: string | null;
  last_error?: string | null;
  created_by_email?: string | null;
  approved_by_email?: string | null;
  approved_at?: string | null;
  submitted_at?: string | null;
}

/** Was die naechste Periodenrechnung nach heutigem Stand enthielte. */
export interface ContractNextInvoice {
  kind: 'period' | 'closing';
  date: string;
  service_start?: string | null;
  service_end?: string | null;
  usage_start?: string | null;
  usage_end?: string | null;
  lines: ContractInvoiceLine[];
  net_cents: number;
}

export interface ManualInvoiceBody {
  lines: { description: string; quantity: number; unit?: string | null; unit_price_net_cents: number }[];
  service_start?: string | null;
  /** Letzter Tag (inklusiv). */
  service_end?: string | null;
  payment_terms_days?: number | null;
  note?: string | null;
}

/** Freigeben/Senden: Draft und gescheiterte Uebergabe. */
export const INVOICE_APPROVABLE: ContractInvoiceStatus[] = ['draft', 'failed'];
/** Storno in alphacount: ausgestellt und nicht bezahlt. */
export const INVOICE_CANCELLABLE: ContractInvoiceStatus[] = ['issued', 'sent', 'overdue', 'dunning'];

export interface ContractAdminPayload {
  client_id: string;
  company_name?: string | null;
  has_stripe_subscription: boolean;
  /** alphacount-Zugang hinterlegt (ALPHACOUNT_API_URL + _KEY). */
  alphacount_configured?: boolean;
  /** Fehlende Rechnungsangaben am Kunden (company_name, street, zip, city, country_code). */
  billing_identity_missing?: string[];
  next_invoice?: ContractNextInvoice | null;
  invoices?: ContractInvoice[];
  current_plan: ContractPlan | null;
  current_limits: ContractLimits;
  contract: ClientContract | null;
  base_plan: ContractPlan | null;
  base_limits: ContractLimits | null;
  templates: ContractTemplatePlan[];
  /** Gespeicherte Vertragsvorlagen. */
  contract_templates?: ContractTemplate[];
  /** Funktionen, die der Kunde heute hat. */
  client_features?: string[];
  limit_keys: string[];
  history: ClientContract[];
  audit: ContractAuditEntry[];
}

/** Body fuer PUT — beim Bearbeiten nur die geaenderten Felder. */
export interface ContractUpsertBody {
  /** Nur beim Aktivieren, beide optional: Standardplan als Startwert und die
   *  angewendete Vertragsvorlage (fuers Protokoll). */
  template_plan_id?: string | null;
  contract_template_id?: string | null;
  features?: string[];
  display_name?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  renewal?: ContractRenewal;
  notice_days?: number | null;
  billing_cycle?: ContractBillingCycle;
  base_fee_net_cents?: number;
  payment_terms_days?: number;
  invoice_email?: string | null;
  buyer_reference?: string | null;
  po_number?: string | null;
  usage_terms?: ContractUsageTerm[];
  invoice_mode?: ContractInvoiceMode;
  billing_starts_on?: string | null;
  note?: string | null;
  limits?: ContractLimits;
}

/** Fehlercode des Backends, wenn ein Vertragskunde einen Stripe-Weg aufruft
 *  (Kauf, Planwechsel, Trial, Kuendigung, Credit-Kauf). */
export const CONTRACT_MANAGED = 'CONTRACT_MANAGED';

@Injectable({
  providedIn: 'root',
})
export class ContractService {
  constructor(protected http: HttpClient) {}

  /** Super-Admin: Vertrag, Vorlagen, Kontingente und Verlauf eines Kunden. */
  getClientContract(clientId: string) {
    return this.http
      .get<{ data: ContractAdminPayload }>(
        `${environment.userBackend}/admin/clients/${clientId}/contract`
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: aktivieren (ohne aktiven Vertrag) oder aendern. */
  saveClientContract(clientId: string, body: ContractUpsertBody) {
    return this.http
      .put<{ data: ContractAdminPayload }>(
        `${environment.userBackend}/admin/clients/${clientId}/contract`,
        body
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: Vertrag beenden; ohne Angabe waehlt das Backend den
   *  Rueckfallplan (vorheriger Plan bei laufendem Stripe-Abo, sonst free). */
  endClientContract(
    clientId: string,
    body: { fallback_plan_id?: string; note?: string | null } = {}
  ) {
    return this.http
      .post<{ data: ContractAdminPayload }>(
        `${environment.userBackend}/admin/clients/${clientId}/contract/end`,
        body
      )
      .pipe(map((res) => res.data));
  }

  // ---------------------------------------------------------------- Vertragsvorlagen

  createContractTemplate(body: ContractTemplateBody) {
    return this.http
      .post<{ data: ContractTemplate }>(`${environment.userBackend}/admin/contract-templates`, body)
      .pipe(map((res) => res.data));
  }

  updateContractTemplate(templateId: string, body: ContractTemplateBody) {
    return this.http
      .put<{ data: ContractTemplate }>(
        `${environment.userBackend}/admin/contract-templates/${templateId}`,
        body
      )
      .pipe(map((res) => res.data));
  }

  deleteContractTemplate(templateId: string) {
    return this.http.delete(`${environment.userBackend}/admin/contract-templates/${templateId}`);
  }

  // ---------------------------------------------------------------- Rechnungen

  /** Super-Admin: manuelle Rechnung als Entwurf (Positionen netto). */
  createManualInvoice(clientId: string, body: ManualInvoiceBody) {
    return this.http
      .post<{ data: ContractInvoice }>(
        `${environment.userBackend}/admin/clients/${clientId}/invoices`,
        body
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: freigeben und an alphacount uebergeben (ausstellen +
   *  versenden). Fehler (503/422/502) zeigt der Aufrufer selbst an — die
   *  Rechnung steht dann auf `failed` und laesst sich erneut freigeben. */
  approveInvoice(invoiceId: string) {
    return this.http
      .post<{ data: ContractInvoice }>(
        `${environment.userBackend}/admin/invoices/${invoiceId}/approve`,
        {},
        { headers: new HttpHeaders({ 'X-Suppress-Error-Toast': '1' }) }
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: Entwurf oder gescheiterte Rechnung verwerfen. */
  discardInvoice(invoiceId: string, note?: string | null) {
    return this.http
      .post<{ data: ContractInvoice }>(
        `${environment.userBackend}/admin/invoices/${invoiceId}/discard`,
        { note: note || null }
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: Storno in alphacount (nur unbezahlte). */
  cancelInvoice(invoiceId: string) {
    return this.http
      .post<{ data: ContractInvoice }>(
        `${environment.userBackend}/admin/invoices/${invoiceId}/cancel`,
        {}
      )
      .pipe(map((res) => res.data));
  }

  /** Super-Admin: PDF aus alphacount. */
  getAdminInvoicePdf(invoiceId: string) {
    return this.http.get(`${environment.userBackend}/admin/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
  }

  /** Kundenansicht: von alphacount ausgestellte Rechnungen. */
  getOwnInvoices() {
    return this.http
      .get<{ data: ContractInvoice[] }>(`${environment.userBackend}/clients/contract/invoices`)
      .pipe(map((res) => res.data));
  }

  /** Kundenansicht: PDF einer eigenen Rechnung. */
  getOwnInvoicePdf(invoiceId: string) {
    return this.http.get(`${environment.userBackend}/clients/contract/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
  }

  /** Kundenansicht (admin, super_admin): eigener Vertrag oder null. */
  getOwnContract() {
    return this.http
      .get<{ data: { contract: ClientContract | null } }>(
        `${environment.userBackend}/clients/contract`
      )
      .pipe(map((res) => res.data.contract));
  }
}

/** Speicher steht in Bytes im Plan, angezeigt und bearbeitet in MB. */
export const BYTES_PER_MB = 1024 * 1024;

/** YYYY-MM-DD aus einem lokalen Datum — ohne toISOString(), das in CET auf
 *  den Vortag rutscht. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** PDF-Blob im neuen Tab oeffnen (Download-Fallback, wenn der Browser den
 *  Tab blockiert). */
export function openPdfBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const tab = window.open(url, '_blank');
  if (!tab) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Letzter Tag eines halboffenen Zeitraums [start, end) als YYYY-MM-DD. */
export function lastDayOf(endExclusive: string | null | undefined): string | null {
  if (!endExclusive) return null;
  const [y, m, d] = endExclusive.slice(0, 10).split('-').map(Number);
  return toIsoDate(new Date(y, m - 1, d - 1));
}
